/**
 * useSession Hook
 *
 * Fetches session data by ID from backend.
 * Supports mock mode for development/testing.
 */

import { useState, useEffect, useCallback } from 'react';
import type { Session } from '../types/session.types';
import { logger } from '../utils/logger';

/**
 * useSession Hook Configuration
 */
export interface UseSessionConfig {
  /**
   * Use mock data instead of real backend
   * Default: false
   */
  useMockData?: boolean;

  /**
   * API base URL
   * Default: '/bff/agent'
   */
  baseUrl?: string;
}

/**
 * useSession Hook Return Type
 */
export interface UseSessionResult {
  /**
   * Session data (available when loaded)
   */
  session: Session | null;

  /**
   * Loading state
   */
  loading: boolean;

  /**
   * Error that occurred during fetch
   */
  error: Error | null;

  /**
   * Retry session fetch
   */
  retry: () => void;
}

/**
 * Mock session data for development
 */
const MOCK_SESSIONS: Record<string, Session> = {
  // CHAT session
  'chat-session-123': {
    sessionId: 'chat-session-123',
    interactionType: 'CHAT',
    customerId: 'cust_abc123',
    amount: 5000, // £50.00
    currency: 'GBP',
    status: 'IN_PROGRESS',
    createdAt: new Date().toISOString(),
    paymentId: 'pay_chat_123',
  },

  // VOICE session
  'voice-session-456': {
    sessionId: 'voice-session-456',
    interactionType: 'VOICE',
    customerId: 'cust_def456',
    amount: 12000, // £120.00
    currency: 'GBP',
    status: 'IN_PROGRESS',
    createdAt: new Date().toISOString(),
    paymentId: 'pay_voice_456',
  },

  // VOICE_MANUAL session
  'voice-manual-789': {
    sessionId: 'voice-manual-789',
    interactionType: 'VOICE_MANUAL',
    customerId: 'cust_ghi789',
    amount: 7500, // £75.00
    currency: 'GBP',
    status: 'IN_PROGRESS',
    createdAt: new Date().toISOString(),
    paymentId: 'pay_manual_789',
  },
};

/**
 * useSession Hook
 *
 * Fetches session data by ID from backend or returns mock data.
 * Caches session data to prevent refetching.
 *
 * @param sessionId - Session identifier
 * @param config - Hook configuration
 * @returns Session state and retry function
 *
 * @example
 * function SessionPage() {
 *   const { session, loading, error, retry } = useSession(sessionId, {
 *     useMockData: true,
 *   });
 *
 *   if (loading) return <Loading />;
 *   if (error) return <Error error={error} onRetry={retry} />;
 *   if (!session) return <NotFound />;
 *
 *   return <PaymentUI session={session} />;
 * }
 */
export function useSession(
  sessionId: string,
  config: UseSessionConfig = {}
): UseSessionResult {
  const { useMockData = false } = config;

  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch session from backend
   */
  const fetchSession = useCallback(async () => {
    if (!sessionId) {
      setError(new Error('Session ID is required'));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Mock mode
      if (useMockData) {
        logger.info('Using mock session data', { sessionId });

        // Simulate network delay
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Check if exact sessionId exists in mock data
        let mockSession = MOCK_SESSIONS[sessionId];

        // If not found, detect interaction type from sessionId pattern
        if (!mockSession) {
          let interactionType: 'CHAT' | 'VOICE' | 'VOICE_MANUAL' = 'VOICE';

          if (sessionId.includes('chat')) {
            interactionType = 'CHAT';
          } else if (sessionId.includes('manual')) {
            interactionType = 'VOICE_MANUAL';
          } else if (sessionId.includes('voice')) {
            interactionType = 'VOICE';
          }

          // Generate mock session based on detected type
          mockSession = {
            sessionId,
            interactionType,
            customerId: 'cust_mock_123',
            amount: 9999, // £99.99
            currency: 'GBP',
            status: 'IN_PROGRESS',
            createdAt: new Date().toISOString(),
            paymentId: `pay_${interactionType.toLowerCase()}_mock`,
          };
        }

        setSession(mockSession);
        setLoading(false);
        return;
      }

      // In production, session data comes from window.__PAYMENT_SESSION__ injected
      // by the Lambda BFF — useSession is only used in mock mode.
      throw new Error(
        'useSession called outside mock mode — use initialSession from window.__PAYMENT_SESSION__ instead'
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch session';
      logger.error('Session fetch failed', err, { sessionId });

      setError(new Error(errorMessage));
      setLoading(false);
    }
  }, [sessionId, useMockData]);

  /**
   * Fetch session on mount or when sessionId changes
   */
  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  /**
   * Retry function
   */
  const retry = useCallback(() => {
    fetchSession();
  }, [fetchSession]);

  return {
    session,
    loading,
    error,
    retry,
  };
}
